import classNames from 'classnames'
import { Button } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faPlay,
  faVideo,
  faCalendar,
  faCircleCheck,
} from '@fortawesome/pro-solid-svg-icons'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import { BlockMeetingContent } from 'store/states/lessons.state'
import { useTranslationRankmi } from 'helpers/translations.helper'
import { hasProtocol } from 'helpers'
import { MandatoryText } from '../../GenerateContent/MandatoryText'
import styles from './Meeting.module.scss'
import {
  formatStarTimeWebinar,
  webinarStartTime,
  webinarEndTime,
} from './helpers'
import WebinarBanner from './components/webinarBadge'

interface MeetingProps {
  data: BlockMeetingContent
}

export const Meeting = ({ data }: MeetingProps) => {
  const t = useTranslationRankmi({
    prefix: 'lessons.contentBlocks.meetingBlock',
  })

  const joinUrl = data.invitation?.joinUrl

  const onJoin = () => {
    if (joinUrl)
      window.open(hasProtocol(joinUrl) ? joinUrl : `//${joinUrl}`, '_blank')
  }

  const isValidDate = (dateValue: string | null | undefined): boolean => {
    if (!dateValue) return false
    const date = new Date(dateValue)
    return !isNaN(date.getTime())
  }

  const meetingHasEnded = () => {
    const startTime = data.startTime
    if (!isValidDate(startTime)) return false
    const now = new Date()
    const start = new Date(startTime!)
    const duration = data.duration
    const end = new Date(start.getTime() + duration * 60000)
    return now >= end
  }

  const meetinghastStarted = () => {
    const startTime = data.startTime
    if (!isValidDate(startTime)) return false
    const now = new Date()
    const start = new Date(startTime!)
    return now >= start
  }

  const meetingIsSoon = !meetinghastStarted()

  const meetingIsLive = meetinghastStarted() && !meetingHasEnded()

  const statesOfMeetingsButton = () => {
    if (meetingIsSoon) {
      return (
        <div className={styles.playIcon}>
          <FontAwesomeIcon className={styles.icon} icon={faCalendar} />
        </div>
      )
    }

    if (meetingIsLive) {
      return (
        <div className={styles.playIcon}>
          <FontAwesomeIcon className={styles.icon} icon={faPlay} />
        </div>
      )
    }

    if (meetingHasEnded()) {
      return (
        <div className={styles.playIcon}>
          <FontAwesomeIcon className={styles.icon} icon={faCircleCheck} />
        </div>
      )
    }
  }

  const stateOfMeetingsMessage = () => {
    if (!isValidDate(data.startTime)) {
      return null
    }

    const dateWebinarFormated = format(
      new Date(data.startTime!),
      'dd/MMM/yyyy',
      {
        locale: es,
      }
    )
    const timeWebinarFormated = formatStarTimeWebinar(data.startTime!)

    if (meetingIsSoon) {
      return (
        <>
          <p className={styles.startDate}>
            {t('stateWebinars.meetingIsSoon.initialDate')} {dateWebinarFormated}
          </p>
          <p className={styles.startTime}>
            {t('stateWebinars.meetingIsSoon.webinarStartTime', {
              startTime: timeWebinarFormated,
              duration: data.duration,
            })}
          </p>
        </>
      )
    }

    if (meetingIsLive) {
      return (
        <>
          <p className={styles.webinarIsLive}>
            {t('stateWebinars.meetingIsLive')}
          </p>
        </>
      )
    }

    if (meetingHasEnded()) {
      return (
        <>
          <p className={styles.startDate}>
            {t('stateWebinars.meetingEndedLabel')}
          </p>
          <p className={styles.startTime}>
            {t('stateWebinars.meetingEnded', {
              startDate: dateWebinarFormated,
              startTime: timeWebinarFormated,
            })}
          </p>
        </>
      )
    }
  }

  const disabled = !joinUrl || meetingHasEnded()
  return (
    <div className={styles.container}>
      <h1>{data.title}</h1>
      <MandatoryText />
      <p>{t('info')}</p>
      {meetingIsLive && (
        <WebinarBanner
          startTime={webinarStartTime(data.startTime)}
          endTime={webinarEndTime(data.startTime, data.duration)}
        />
      )}
      <div className={styles.meeting}>
        <div className="text-center">
          <div className="w-100 d-flex justify-content-center">
            <Button
              className={classNames('negative-primary', styles.playCircle)}
              onClick={onJoin}
              disabled={disabled}
            >
              {statesOfMeetingsButton()}
            </Button>
          </div>
          {stateOfMeetingsMessage()}
          {meetingIsLive && (
            <div className="d-flex justify-content-center">
              <Button
                size="lg"
                className={styles.button}
                onClick={onJoin}
                disabled={disabled}
              >
                <FontAwesomeIcon className={styles.icon} icon={faVideo} />
                {t('player.button')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
